// useAutoAttachClips.ts

import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../store/store';
import { getLiteImages } from '../../../rtk-apis/reports/getLiteImages';
import { updateReportClips, AttachedClip } from '../../../rtk-apis/reports/updateReportClips';
import { buildClipsFromLiteImages } from './attachClipsHelpers';

/**
 * Name given to the shared-clips link when nobody has typed one.
 *
 * Kevin, 1 Sep: "by default name it as clips". Without a name the shared-clips section
 * printed the raw URL, which is what the clinician and the GP both see.
 */
const DEFAULT_SHARED_CLIPS_NAME = 'Clips';

type AutoAttachArgs = {
  /** Report the clips belong to. Nothing runs until this exists. */
  reportId?: string | null;
  /** Study the clips come from. Nothing runs without it. */
  studyInstanceUID?: string | null;
  /** Clips already on the report. */
  attachedClips: unknown[];
  /**
   * Set once clips have EVER been written to this report — the backend stamps it
   * alongside `sharedClipsId` on the first save and never clears it. This, not the
   * clip count, is what tells an untouched report apart from one the clinician
   * deliberately emptied.
   */
  sharedClipsUrl?: string | null;
  /** False on a read-only report (finalized, or not this clinician's). */
  enabled: boolean;
  /** Called once, with the saved set, so the surrounding page can show it. */
  onAttached: (clips: AttachedClip[], shareUrl: string | null, sharedClipsName: string) => void;
};

/**
 * Attach every clip in the study to a new report, without anyone opening the modal.
 *
 * Nikhil, 1 Sep: "can you also make attaching clips automatic please. and instead of
 * attach clips text, have like change clips". The modal already pre-selected all clips,
 * so the manual step was open → Save with nothing changed, on every single report. The
 * button it leaves behind is a CHANGE, not an attach.
 *
 * RUNS AT MOST ONCE PER REPORT, AND ONLY ON A REPORT THAT HAS NEVER HAD CLIPS WRITTEN.
 * Three guards, because this writes to the report on its own:
 *
 *  1. `sharedClipsUrl` — a report that has been through the clip save once is never
 *     touched again. Without this, a clinician who removed clips on purpose would find
 *     them all back on the next open, with no way to make the removal stick.
 *  2. `attachedClips.length` — belt and braces for a report holding clips but somehow
 *     missing the URL.
 *  3. `startedRef` — the props feeding this hook change on every refetch. Keyed by
 *     report id so opening a second report in the same mount still works.
 *
 * FAILURE IS SILENT AND FINAL. A clip attach is a convenience; it must never surface an
 * error over the report or retry in a loop while the clinician is typing. The button
 * remains, so the manual path is always there.
 */
export function useAutoAttachClips({
  reportId,
  studyInstanceUID,
  attachedClips,
  sharedClipsUrl,
  enabled,
  onAttached,
}: AutoAttachArgs) {
  const dispatch = useDispatch<AppDispatch>();
  /**
   * Report id currently being attached, or already attached, in this mount.
   *
   * A ref and not state, and deliberately NOT reset by the effect's cleanup — see the
   * long note in the effect.
   */
  const startedRef = useRef<string | null>(null);

  // Read through a ref: the callback is rebuilt on every render of the calling page,
  // and listing it as a dependency would re-run this effect constantly.
  const onAttachedRef = useRef(onAttached);
  onAttachedRef.current = onAttached;

  const alreadyHasClips = Array.isArray(attachedClips) && attachedClips.length > 0;
  const everSaved = !!sharedClipsUrl;

  useEffect(() => {
    // Left in on purpose while this is being verified on live studies. Every gate that
    // can stop the attach is printed, so a report that does not get its clips says why
    // in one line instead of failing silently.
    console.log('[auto-attach clips] gate', {
      enabled,
      reportId,
      studyInstanceUID,
      everSaved,
      alreadyHasClips,
      alreadyStarted: startedRef.current === reportId,
    });

    if (!enabled || !reportId || !studyInstanceUID) return;
    if (everSaved || alreadyHasClips) return;
    if (startedRef.current === reportId) return;

    startedRef.current = reportId;

    /**
     * NO `mounted` FLAG AROUND THIS SEQUENCE, AND NO CLEANUP THAT CANCELS IT.
     *
     * The first version of this hook did exactly that, and it meant the clips were never
     * attached at all (found on a live 89-clip study, 1 Sep). React runs an effect's
     * cleanup on every RE-RUN, not only on unmount, and this effect re-runs often: the
     * calling page's props effect writes `attachedClips` and `sharedClipsUrl` on every
     * refetch, which recomputes two of the dependencies below. So the flag flipped while
     * `getLiteImages` was still in flight, the guard after the await returned early, and
     * `updateReportClips` was never dispatched — silently, with `startedRef` still set, so
     * it never retried either. The bigger the study, the slower the fetch, the more
     * certain the failure.
     *
     * Once the decision to attach has been made, the write must complete. It is a PATCH
     * of two fields on a report that exists; there is nothing about this page still being
     * mounted that makes it wrong to finish. The only thing worth skipping on unmount is
     * the state callback at the end, and calling setState on an unmounted component is a
     * no-op in React 18 — no warning, nothing to guard.
     */
    (async () => {
      try {
        const response: any = await dispatch(getLiteImages({ studyInstanceUID })).unwrap();

        const clips = buildClipsFromLiteImages(Array.isArray(response?.data) ? response.data : []);
        // A study whose conversion pipeline has not produced lite images yet. Saving an
        // empty set would stamp `sharedClipsUrl` and permanently disable the automatic
        // attach for this report, so leave it alone and let the next open try again.
        if (!clips.length) {
          startedRef.current = null;
          return;
        }

        const result: any = await dispatch(
          updateReportClips({
            reportId,
            attachedClips: clips,
            sharedClipsName: DEFAULT_SHARED_CLIPS_NAME,
          })
        ).unwrap();

        console.log('[auto-attach clips] saved', {
          count: clips.length,
          sharedClipsUrl: result?.data?.sharedClipsUrl,
          sharedClipsName: result?.data?.sharedClipsName,
        });

        onAttachedRef.current(
          clips,
          result?.data?.sharedClipsUrl || null,
          result?.data?.sharedClipsName || DEFAULT_SHARED_CLIPS_NAME
        );
      } catch (error) {
        // Cleared so a later run can try again — a failed attach must not lock the report
        // out of ever getting its clips.
        startedRef.current = null;
        console.warn('[auto-attach clips] failed:', error);
      }
    })();
  }, [dispatch, enabled, reportId, studyInstanceUID, everSaved, alreadyHasClips]);
}
